// frontend/components/features/CredentialDetailModal.tsx
"use client";

import { useEffect, useRef } from "react";
import {
  X,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  ExternalLink,
  Calendar,
  MapPin,
  Hash,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  type NurseCredential,
  type CredentialStatus,
  type ExpiryStatus,
} from "@/lib/hooks/useNurseCredentials";

// =============================================================================
// Status Badge Components
// =============================================================================

function CredentialStatusBadge({ status }: { status: CredentialStatus }) {
  const config = {
    VERIFIED: {
      icon: CheckCircle,
      label: "Verified",
      className: "bg-emerald-100 text-emerald-700",
    },
    PENDING: {
      icon: Clock,
      label: "Pending",
      className: "bg-amber-100 text-amber-700",
    },
    REJECTED: {
      icon: XCircle,
      label: "Rejected",
      className: "bg-red-100 text-red-700",
    },
    EXPIRED: {
      icon: AlertTriangle,
      label: "Expired",
      className: "bg-slate-100 text-slate-700",
    },
  };

  const { icon: Icon, label, className } = config[status];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium",
        className
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </span>
  );
}

function ExpiryStatusBadge({ status }: { status: ExpiryStatus }) {
  if (status === "VALID") return null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium",
        status === "EXPIRING_SOON" && "bg-amber-100 text-amber-700",
        status === "EXPIRED" && "bg-red-100 text-red-700"
      )}
    >
      <AlertTriangle className="h-4 w-4" />
      {status === "EXPIRING_SOON" ? "Expiring Soon" : "Expired"}
    </span>
  );
}

// =============================================================================
// Component Props
// =============================================================================

export interface CredentialDetailModalProps {
  credential: NurseCredential;
  onClose: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function CredentialDetailModal({ credential, onClose }: CredentialDetailModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Determine file type for preview
  const getFileType = (url: string | null): "pdf" | "image" | "other" | null => {
    if (!url) return null;
    const extension = url.split(".").pop()?.toLowerCase();
    if (extension === "pdf") return "pdf";
    if (["jpg", "jpeg", "png"].includes(extension || "")) return "image";
    return "other";
  };

  const fileType = getFileType(credential.document_url);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-slate-200">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-slate-900">
              {credential.credential_type.name}
            </h2>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <CredentialStatusBadge status={credential.status} />
              <ExpiryStatusBadge status={credential.expiry_status} />
              {credential.credential_type.is_license && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                  License
                </span>
              )}
              {credential.credential_type.required_for_work && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                  Required
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid lg:grid-cols-2 gap-6 p-6">
            {/* Details Column */}
            <div className="space-y-6">
              {/* Credential Information */}
              <section>
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                  Credential Information
                </h3>
                <div className="space-y-3">
                  {credential.license_number && (
                    <div className="flex items-start gap-3">
                      <Hash className="h-4 w-4 text-slate-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-slate-500">License/Certificate Number</div>
                        <div className="text-sm font-medium text-slate-900">
                          {credential.license_number}
                        </div>
                      </div>
                    </div>
                  )}

                  {credential.issuing_state && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-slate-500">Issuing State</div>
                        <div className="text-sm font-medium text-slate-900">
                          {credential.issuing_state}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <Calendar className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-500">Issue Date</div>
                      <div className="text-sm font-medium text-slate-900">
                        {formatDate(credential.issued_at)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-500">Expiration Date</div>
                      <div
                        className={cn(
                          "text-sm font-medium",
                          credential.expiry_status === "EXPIRED" && "text-red-600",
                          credential.expiry_status === "EXPIRING_SOON" && "text-amber-600",
                          credential.expiry_status === "VALID" && "text-slate-900"
                        )}
                      >
                        {formatDate(credential.expires_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Type Information */}
              {credential.credential_type.description && (
                <section>
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                    About This Credential
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {credential.credential_type.description}
                  </p>
                </section>
              )}

              {/* Verification History */}
              <section>
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                  Verification History
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Uploaded:</span>
                    <span className="font-medium text-slate-900">
                      {formatDateTime(credential.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Last Updated:</span>
                    <span className="font-medium text-slate-900">
                      {formatDateTime(credential.updated_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Current Status:</span>
                    <CredentialStatusBadge status={credential.status} />
                  </div>
                </div>
              </section>

              {/* Document Actions */}
              {credential.document_url && (
                <section>
                  <a
                    href={credential.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Document in New Tab
                  </a>
                </section>
              )}
            </div>

            {/* Document Preview Column */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                Document Preview
              </h3>

              {!credential.document_url ? (
                <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                  <FileText className="mx-auto h-12 w-12 text-slate-300" />
                  <p className="mt-2 text-sm text-slate-500">No document uploaded</p>
                </div>
              ) : fileType === "pdf" ? (
                <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                  <iframe
                    src={credential.document_url}
                    className="w-full h-[500px]"
                    title={`${credential.credential_type.name} Document`}
                  />
                </div>
              ) : fileType === "image" ? (
                <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                  <img
                    src={credential.document_url}
                    alt={credential.credential_type.name}
                    className="w-full h-auto"
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
                  <FileText className="mx-auto h-12 w-12 text-slate-400" />
                  <p className="mt-2 text-sm text-slate-600">
                    Preview not available for this file type
                  </p>
                  <a
                    href={credential.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Document
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
